import type { FlowLink, FlowNode } from "../../design";
import type { Flow, FlowMeasure } from "../../lib/types";
import { humanize, usdShort } from "./util";

const GROUPS = 8;
const MODELS = 5;
const MODEL_COLORS = [
  "var(--ds-s1)",
  "var(--ds-s2)",
  "var(--ds-s3)",
  "var(--ds-s4)",
  "var(--ds-s5)",
];

export function formatMeasure(measure: FlowMeasure, v: number): string {
  if (measure === "cost") return usdShort(v);
  if (measure === "requests")
    return `${humanize(v)} ${v === 1 ? "request" : "requests"}`;
  return `${humanize(v)} tokens`;
}

const BILLED = "Billed per token";
const PLAN = "Covered by a plan";
const UNKNOWN = "Billing unrecognised";
const BASIS_NODE: Record<string, string> = {
  api_equivalent: "b:plan",
  unknown: "b:unknown",
};
const BASIS_LABEL: Record<string, string> = {
  "b:plan": PLAN,
  "b:unknown": UNKNOWN,
};

type Row = Flow["rows"][number];

/** How badly bands cross between two columns: every pair of links whose
 * ends swap order, weighted by both widths. */
export function crossings(
  links: FlowLink[],
  from: Map<string, number>,
  to: Map<string, number>,
): number {
  const ls = links.filter((l) => from.has(l.source) && to.has(l.target));
  let sum = 0;
  for (let i = 0; i < ls.length; i++)
    for (let j = i + 1; j < ls.length; j++) {
      const a = from.get(ls[i].source)! - from.get(ls[j].source)!;
      const b = to.get(ls[i].target)! - to.get(ls[j].target)!;
      if (a * b < 0) sum += ls[i].value * ls[j].value;
    }
  return sum;
}

const index = (ids: string[]) => new Map(ids.map((id, i) => [id, i]));

/** ``ids`` (ranked by size) moved beside the nodes in ``at`` that feed
 * them, when that clearly cuts how much the bands cross. */
function settle(
  ids: string[],
  links: FlowLink[],
  at: Map<string, number>,
): string[] {
  const centre = new Map<string, number>();
  for (const id of ids) {
    const into = links.filter((l) => l.target === id && at.has(l.source));
    const w = into.reduce((s, l) => s + l.value, 0);
    centre.set(
      id,
      w > 0
        ? into.reduce((s, l) => s + at.get(l.source)! * l.value, 0) / w
        : Infinity,
    );
  }
  const moved = [...ids].sort((a, b) => centre.get(a)! - centre.get(b)!);
  const before = crossings(links, at, index(ids));
  const after = crossings(links, at, index(moved));
  return after < before * 0.8 ? moved : ids;
}

/** The rows as three columns: the top groups, the top models, how it's
 * paid. Each column ranks by size, unless moving models beside the groups
 * that use them (or billing beside its models) uncrosses the bands.
 * ``none`` names the rows with no group. */
export function flowGraph(
  rows: Flow["rows"],
  none = "Unattributed",
): {
  nodes: FlowNode[];
  links: FlowLink[];
} {
  const sum = (key: (r: Row) => string) => {
    const m = new Map<string, number>();
    for (const r of rows) m.set(key(r), (m.get(key(r)) ?? 0) + r.value);
    return m;
  };
  const ranked = (m: Map<string, number>) =>
    [...m.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k);
  const groupLabel = new Map(rows.map((r) => [r.key ?? "", r.label]));
  const groups = ranked(sum((r) => r.key ?? ""));
  const topGroups = new Set(groups.slice(0, GROUPS));
  const models = ranked(sum((r) => r.model ?? ""));
  const topModels = new Set(models.slice(0, MODELS));
  const g = (r: Row) =>
    topGroups.has(r.key ?? "") ? `g:${r.key ?? ""}` : "g:__other__";
  const m = (r: Row) =>
    topModels.has(r.model ?? "") ? `m:${r.model ?? ""}` : "m:__other__";
  const b = (r: Row) => BASIS_NODE[r.cost_basis ?? ""] ?? "b:billed";

  const links = new Map<string, FlowLink>();
  const add = (source: string, target: string, value: number) => {
    const k = `${source}>${target}`;
    const l = links.get(k) ?? { source, target, value: 0 };
    l.value += value;
    links.set(k, l);
  };
  for (const r of rows) {
    add(g(r), m(r), r.value);
    add(m(r), b(r), r.value);
  }
  const all = [...links.values()];

  const restGroups = groups.length - topGroups.size;
  const restModels = models.length - topModels.size;
  const groupIds = [
    ...groups.slice(0, GROUPS).map((k) => `g:${k}`),
    ...(restGroups > 0 ? ["g:__other__"] : []),
  ];
  const modelIds = settle(
    models.slice(0, MODELS).map((k) => `m:${k}`),
    all,
    index(groupIds),
  );
  const unknown = all.some((l) => l.target === "b:unknown");
  const billing = settle(
    ["b:billed", "b:plan", ...(unknown ? ["b:unknown"] : [])],
    all,
    index([...modelIds, "m:__other__"]),
  );
  const colour = new Map(
    models.slice(0, MODELS).map((k, i) => [`m:${k}`, MODEL_COLORS[i]]),
  );

  const nodes: FlowNode[] = [
    ...groups.slice(0, GROUPS).map((k) => ({
      id: `g:${k}`,
      label: groupLabel.get(k) || none,
      column: 0,
    })),
    ...(restGroups > 0
      ? [{ id: "g:__other__", label: `Other (${restGroups})`, column: 0 }]
      : []),
    ...modelIds.map((id) => ({
      id,
      label: id.slice(2) || "Unknown model",
      column: 1,
      color: colour.get(id),
    })),
    ...(restModels > 0
      ? [
          {
            id: "m:__other__",
            label: `Other models (${restModels})`,
            column: 1,
            color: "var(--ds-other)",
          },
        ]
      : []),
    ...billing.map((id) => ({
      id,
      label: BASIS_LABEL[id] ?? BILLED,
      column: 2,
    })),
  ];
  return { nodes, links: all };
}
